/**
 * ========================================
 * FEATURE SERVICE
 * ========================================
 * API calls for the master feature catalog (public read) and its admin CRUD
 * + plan-feature assignment endpoints.
 * Endpoints: /api/v1/features/*, /api/v1/admin/payments/plans/{id}/features
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import { snakeToCamelDeep } from './case-transform';
import type { Feature, PlanFeature, PlanFeatureChecklistItem } from '@/types/payment';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
}

interface FeatureAssignPayload {
  feature_id: string;
  is_enabled: boolean;
  value?: Record<string, any> | null;
}

class FeatureService {
  private baseUrl = '/api/v1/features';
  private adminPlansUrl = '/api/v1/admin/payments/plans';

  // ── Public catalog ────────────────────────────────────────

  async listFeatures(): Promise<Feature[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<Feature[]>>(this.baseUrl);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  // ── Admin: master catalog CRUD ────────────────────────────

  async adminListFeatures(): Promise<Feature[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<Feature[]>>(`${this.baseUrl}/admin`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminCreateFeature(payload: Record<string, any>): Promise<Feature> {
    try {
      const response = await apiClient.post<BackendEnvelope<Feature>>(`${this.baseUrl}/admin`, payload);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminUpdateFeature(featureId: string, payload: Record<string, any>): Promise<Feature> {
    try {
      const response = await apiClient.put<BackendEnvelope<Feature>>(`${this.baseUrl}/admin/${featureId}`, payload);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminSetFeatureActive(featureId: string, active: boolean): Promise<Feature> {
    try {
      const response = await apiClient.post<BackendEnvelope<Feature>>(`${this.baseUrl}/admin/${featureId}/${active ? 'enable' : 'disable'}`, {});
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminDeleteFeature(featureId: string): Promise<Feature> {
    try {
      const response = await apiClient.delete<BackendEnvelope<Feature>>(`${this.baseUrl}/admin/${featureId}`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  // ── Admin: plan feature assignment ────────────────────────

  async adminGetPlanFeatures(planId: string): Promise<PlanFeatureChecklistItem[]> {
    try {
      const response = await apiClient.get<BackendEnvelope<PlanFeatureChecklistItem[]>>(`${this.adminPlansUrl}/${planId}/features`);
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }

  async adminSavePlanFeatures(planId: string, assignments: FeatureAssignPayload[]): Promise<PlanFeature[]> {
    try {
      const response = await apiClient.put<BackendEnvelope<PlanFeature[]>>(`${this.adminPlansUrl}/${planId}/features`, { features: assignments });
      return snakeToCamelDeep(response.data.data);
    } catch (error) {
      return handleAPIError(error);
    }
  }
}

export const featureService = new FeatureService();
export default featureService;
